import { BoltOutlined, SolarPowerOutlined } from '@mui/icons-material';

import usePpaSiteData from '../../hooks/usePpaSiteData';
import SolarForecastBarsCard from '../solar/SolarForecastBarsCard';
import SolarProjectKpiCard from '../solar/SolarProjectKpiCard';
import SolarShareCard from '../solar/SolarShareCard';
import SolarTouSavingsCard from '../solar/SolarTouSavingsCard';

export type SolarSiteView = 'kpis' | 'yield' | 'share' | 'demand' | 'savings';

interface Props {
  view: SolarSiteView;
}

/** One view of the selected PPA site's solar performance. */
const SolarSiteWidget = ({ view }: Props) => {
  const { status, site } = usePpaSiteData();
  const months = site?.months ?? [];
  const keys = months.map((m) => m.key);
  const hasData = months.map((m) => m.hasSolarData);
  const hasForecast = (site?.meterPoints.forecast ?? 0) > 0;
  const hasMunicipal = (site?.meterPoints.municipal ?? 0) > 0;

  switch (view) {
    case 'kpis':
      return (
        <SolarProjectKpiCard
          status={status}
          cumulative={site?.cumulative}
          hasForecast={hasForecast}
          hasMunicipal={hasMunicipal}
        />
      );
    case 'yield':
      return (
        <SolarForecastBarsCard
          status={status}
          title='Solar PV Monthly Energy Yield'
          avatar={<SolarPowerOutlined />}
          keys={keys}
          hasData={hasData}
          actual={months.map((m) => m.solarKwh)}
          forecast={hasForecast ? months.map((m) => m.forecastKwh) : undefined}
          unit='kWh'
          actualName='Actual yield'
          forecastName='Forecast yield'
          decimals={0}
        />
      );
    case 'share':
      return (
        <SolarShareCard
          status={status}
          months={months}
          hasMunicipal={hasMunicipal}
        />
      );
    case 'demand':
      return (
        <SolarForecastBarsCard
          status={status}
          title='Solar PV Monthly Demand Reduction'
          avatar={<BoltOutlined />}
          keys={keys}
          hasData={hasData}
          actual={months.map((m) => m.demandReductionKva)}
          forecast={
            hasForecast
              ? months.map((m) => m.forecastDemandReductionKva)
              : undefined
          }
          unit='kVA'
          actualName='Actual reduction'
          forecastName='Forecast reduction'
          decimals={1}
        />
      );
    case 'savings':
      return (
        <SolarTouSavingsCard status={status} touSavings={site?.touSavings} />
      );
  }
};

export default SolarSiteWidget;
